/**
 * Profile normalization utilities
 * Ensures consistent profile data structure across the application
 */

/**
 * Normalizes a profile object to have a consistent structure
 * The API returns 'avatarUrl' so we standardize on that field name
 *
 * @param {Object} profile - Profile object with potentially inconsistent field names
 * @returns {Object} Normalized profile object with consistent structure
 */
export const normalizeProfile = (profile) => {
  if (!profile) return null;

  // Ensure avatarUrl field exists (API standard)
  // Fallback to 'avatar' field if 'avatarUrl' doesn't exist (legacy support)
  const avatarUrl = profile.avatarUrl || profile.avatar || null;

  return {
    ...profile,
    avatarUrl, // Canonical field
    // Keep legacy field for backwards compatibility during transition
    avatar: avatarUrl,
  };
};

/**
 * Gets the avatar URL from a profile object
 * Single source of truth for avatar URL retrieval
 *
 * @param {Object} profile - Profile object
 * @returns {string} Avatar URL or default
 */
export const getProfileAvatar = (profile) => {
  if (!profile) return "/default-avatar.png";

  // avatarUrl is the canonical field
  return profile.avatarUrl || profile.avatar || "/default-avatar.png";
};

/**
 * Gets the current profile from sessionStorage with localStorage fallback
 * sessionStorage is cleared on refresh, so we fallback to localStorage
 *
 * @returns {Object|null} Normalized profile object or null
 */
export const getCurrentProfile = () => {
  try {
    // Try sessionStorage first (faster)
    let profileJson = sessionStorage.getItem("selectedProfile");

    // Fallback to localStorage if sessionStorage is empty (e.g., after refresh)
    if (!profileJson) {
      profileJson = localStorage.getItem("selectedProfile");

      // If found in localStorage, sync back to sessionStorage
      if (profileJson) {
        sessionStorage.setItem("selectedProfile", profileJson);
      }
    }

    if (!profileJson) return null;

    const profile = JSON.parse(profileJson);

    return normalizeProfile(profile);
  } catch (error) {
    console.error("Error reading profile from storage:", error);

    return null;
  }
};

/**
 * Saves profile to sessionStorage with normalization
 *
 * @param {Object} profile - Profile object to save
 */
export const saveCurrentProfile = (profile) => {
  if (!profile) return;

  const normalized = normalizeProfile(profile);

  sessionStorage.setItem("selectedProfile", JSON.stringify(normalized));

  // Also save avatar URL separately for quick access
  if (normalized.avatarUrl) {
    sessionStorage.setItem("userAvatar", normalized.avatarUrl);
  }
};
